import React from 'react';
import moment from 'moment';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../../../components/Rodal/Rodal';
import { MeetingObject } from '../../../../../slices/marketplace/meetings/models/meeting-object.model';
import {
    MeetingFormat,
    MeetingFormatTypesOptions,
} from '../../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { TimelineParty } from '../../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { EngagementInPersonMeetingParticipant } from '../../../../../slices/marketplace/meetings/models/engagement-in-person-meeting-participant';
import { EngagementConferenceParticipant } from '../../../../../slices/marketplace/meetings/models/engagement-conference-participant';
import { useStateSelector } from '../../../../../store/selectors';
import { USER_ROLES } from '../../../../../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { LevelOfService } from '../../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import './EngagementMeetingFullInfoModal.scss';

interface EngagementMeetingFullInfoModalProps {
    isVisible: boolean;
    onClose: () => void;
    event: MeetingObject;
    party?: TimelineParty;
    buyerName: string;
    practiceName?: string;
}

type ParticipantEntry = { name: string; isInPerson: boolean };

function buildParticipantColumns(
    inPersonList: EngagementInPersonMeetingParticipant[],
    virtualList: EngagementConferenceParticipant[]
): { seller: ParticipantEntry[]; buyer: ParticipantEntry[] } {
    const seller: ParticipantEntry[] = [];
    const buyer: ParticipantEntry[] = [];

    inPersonList.forEach((p) => {
        const entry = { name: p.name, isInPerson: true };
        if (p.party === TimelineParty.Seller) seller.push(entry);
        else if (p.party === TimelineParty.Buyer) buyer.push(entry);
    });

    virtualList.forEach((p) => {
        const entry = { name: p.name, isInPerson: false };
        if (p.engagementRole === TimelineParty.Seller) seller.push(entry);
        else if (p.engagementRole === TimelineParty.Buyer) buyer.push(entry);
    });

    return { seller, buyer };
}

const EngagementMeetingFullInfoModal = (
    props: EngagementMeetingFullInfoModalProps
) => {
    const { event, party, buyerName, practiceName } = props;
    const summary = useStateSelector((state) => state.userSummary.summary);
    const currentEngagement = useStateSelector((state) => state.timelines.currentTimeline);
    const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
    const isSfas = currentEngagement?.levelOfService === LevelOfService.StrategicFinancialAdvisoryService;

    let titleString =
        props.party == TimelineParty.Buyer
            ? event.engagementId
                ? `[OppId ${event.engagementId}]`
                : `${event.name}`
            : event.name;

    if (titleString == '') {
        titleString = `Meeting with ${buyerName}`;
    }

    const eventStart = moment(event.dateStart);
    const eventEnd = moment(event.dateEnd);

    const showBadge = event.format === MeetingFormat.Mixed;

    const shouldShowLocation = event.format !== MeetingFormat.Virtual;
    const locationText = shouldShowLocation
        ? event.address
            ? `${event.address.address ? `${event.address.address}, ` : ''}${event.address.city || 'N/A'}, ${event.address.state || 'N/A'}`
            : 'N/A'
        : null;

    const { seller: sellerParticipants, buyer: buyerParticipants } =
        buildParticipantColumns(
            event.inPersonMeetingParticipants || [],
            event.participants || []
        );

    const hasParticipants =
        sellerParticipants.length > 0 || buyerParticipants.length > 0;

    const sellerColumnHeader =
        party !== TimelineParty.Buyer ? 'Our Attendees' : practiceName || 'Seller Attendees';
    const buyerColumnHeader =
        party !== TimelineParty.Buyer ? buyerName || 'Buyer Attendees' : 'Our Attendees';

    return (
        <Rodal
            visible={props.isVisible}
            onClose={props.onClose}
            width={560}
            animation="fade"
            centered>
            <ModalHeader>{titleString}</ModalHeader>
            <ModalBody>
                <div className="meeting-full-info">
                    <div className="meeting-full-info-row meeting-full-info-meta">
                        <span className="meeting-full-info-format">
                            {MeetingFormatTypesOptions[event.format]}
                        </span>
                        <span className="meeting-full-info-time">
                            {eventStart.format('MMM D, YYYY')}&nbsp;&nbsp;
                            {eventStart.format('h:mm A')} -{' '}
                            {eventEnd.format('h:mm A')}
                        </span>
                    </div>
                    {shouldShowLocation && (
                        <div className="meeting-full-info-row meeting-full-info-meta">
                            <span className="meeting-full-info-location">
                                {locationText}
                            </span>
                        </div>
                    )}

                    {event.isCancelled && (
                        <div className="meeting-full-info-cancelled-row">
                            <div className="meeting-full-info-cancelled">
                                Cancelled
                            </div>
                            {!isSfas && (isAdmin || party === TimelineParty.Buyer) && (() => {
                                const paid = event.pricePaid;
                                const refunded = event.refundedAmount;
                                const isFullyRefunded = paid != null && paid > 0 && refunded != null && refunded >= paid;
                                const isPartiallyRefunded = refunded != null && refunded > 0 && refunded < paid;
                                if (isFullyRefunded) return (
                                    <span className="refunded-badge">
                                        <span className="refunded-badge-icon">
                                            <FontAwesomeIcon icon={faCheck} />
                                        </span>
                                        Refunded
                                    </span>
                                );
                                if (isPartiallyRefunded) return (
                                    <span className="refunded-badge">
                                        Partially Refunded
                                    </span>
                                );
                                return null;
                            })()}
                        </div>
                    )}

                    {hasParticipants && (
                        <div className="meeting-full-info-participants">
                            <div className="full-info-participants-column">
                                <div className="full-info-participants-header">
                                    {sellerColumnHeader}
                                </div>
                                {sellerParticipants.map((p, i) => (
                                    <div
                                        key={i}
                                        className="full-info-participant-entry">
                                        <span className="full-info-participant-name">
                                            {p.name}
                                        </span>
                                        {showBadge && (
                                            <span className="full-info-participant-badge">
                                                ({p.isInPerson ? 'IP' : 'V'})
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="full-info-participants-column">
                                <div className="full-info-participants-header">
                                    {buyerColumnHeader}
                                </div>
                                {buyerParticipants.map((p, i) => (
                                    <div
                                        key={i}
                                        className="full-info-participant-entry">
                                        <span className="full-info-participant-name">
                                            {p.name}
                                        </span>
                                        {showBadge && (
                                            <span className="full-info-participant-badge">
                                                ({p.isInPerson ? 'IP' : 'V'})
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </ModalBody>
        </Rodal>
    );
};

export default EngagementMeetingFullInfoModal;
